import { useEffect, useState } from 'react';

import { createAtlAttributionHeader } from '../../../../common/fetch/atl-attribution-header';
import { fetchPersistedAggResponse } from '../../../../common/fetch/fetch-persisted-agg-response';
import { logger } from '../../../../common/utils/logger';
import type { NaturalLanguageToJqlQueryResponseType, NaturalLanguageToJqlQueryType } from './types';

const OPERATION_NAME = 'useNaturalLanguageToJqlQuery';

const fetchNaturalLanguageToJqlQuery = async ({
	variables,
	workspaceId,
	product,
}: {
	variables: NaturalLanguageToJqlQueryType;
	workspaceId: string;
	product: string;
}): Promise<NaturalLanguageToJqlQueryResponseType | undefined> => {
	return fetchPersistedAggResponse<NaturalLanguageToJqlQueryType, any>({
		variables,
		operationName: OPERATION_NAME,
		aggUrl:
			'/gateway/api/graphql/slow/pq/0d32e3740e78f81c3620f117dc1c654e0d259815cecf53bfdaf62665b57d126b',
		headers: {
			'X-ExperimentalApi': 'JiraIssueSearch',
			'x-atlassian-capability': 'NIN_NEW_ISSUE_NAVIGATOR--other',
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: variables.cloudId,
				product,
			}),
		},
		cache: {
			key: `${OPERATION_NAME}.${variables.naturalLanguageInput}`,
			ttl: 5 * 60 * 1000,
		},
	}).then((response) => response.data);
};

type UseNaturalLanguageToJqlQueryParams = {
	cloudId: string;
	query: string;
	workspaceId: string;
	product: string;
	/** When false, no request is sent and the hook returns idle state. Default true. */
	enabled?: boolean;
};

export const useNaturalLanguageToJqlQuery: ({
	cloudId,
	query,
	workspaceId,
	product,
}: UseNaturalLanguageToJqlQueryParams) => {
	jql: string | undefined;
	loading: boolean;
	error: boolean;
} = ({
	cloudId,
	query,
	workspaceId,
	product,
	enabled = true,
}: UseNaturalLanguageToJqlQueryParams) => {
	const [loading, setIsLoading] = useState(true);
	const [error, setError] = useState(false);
	const [jql, setJql] = useState<string | undefined>();

	useEffect(() => {
		if (!enabled) {
			setIsLoading(false);
			setError(false);
			setJql(undefined);
			return;
		}

		const fetchData = async () => {
			let responseError: Error | undefined;
			try {
				setIsLoading(true);

				const data = await fetchNaturalLanguageToJqlQuery({
					variables: {
						cloudId,
						naturalLanguageInput: query,
						iteration: 'ITERATION_2',
						project: null,
					},
					workspaceId,
					product,
				});

				const generatedJQL = data?.jira?.naturalLanguageToJql?.generatedJQL || undefined;
				if (data?.jira?.naturalLanguageToJql?.generatedJQLError) {
					setError(true);
					responseError = new Error(data.jira.naturalLanguageToJql.generatedJQLError.message);
					throw responseError;
				}

				if (data?.errors?.length) {
					setError(true);
					responseError = new Error(data.errors[0].message || 'Unknown error');
					throw responseError;
				}

				// catch all
				if (!Boolean(generatedJQL)) {
					setError(true);
					throw new Error('No JQL generated');
				}

				setJql(generatedJQL);
			} catch (e) {
				logger.error({
					feature: 'jira-nlq-to-smartlink',
					element: 'container',
					operation: 'generate-jql',
					category: responseError && e === responseError ? 'graphql' : 'unknown',
					error: responseError && e === responseError ? undefined : e,
					legacyError: e,
					message: 'Natural language to JQL request failed',
					functionName: 'useNaturalLanguageToJqlQuery',
				});
				setError(true);
			} finally {
				setIsLoading(false);
			}
		};

		if (!!cloudId) {
			fetchData();
		}
	}, [enabled, cloudId, query, product, workspaceId]);

	return {
		jql,
		loading,
		error,
	};
};
